import React, { useEffect, useState } from "react";
import { api, formatApiError, todayStr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineItemsEditor, emptyLine, linesValid, selectCls } from "@/components/stock/LineItemsEditor";
import { toast } from "sonner";

export const newPO = (lines = [emptyLine()], extra = {}) => ({
  po_number: `PO-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000 + 1000)}`,
  vendor: "", order_date: todayStr(), expected_date: "", status: "open", notes: "", lines, ...extra,
});

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export const PODialog = ({ initial, onClose, items, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (initial) setForm({ ...initial, expected_date: (initial.expected_date || "").slice(0, 10), order_date: (initial.order_date || "").slice(0, 10) });
  }, [initial]);
  if (!form) return null;
  const editing = !!initial?.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.po_number) return toast.error(t("po_number"));
    if (!linesValid(form.lines)) return toast.error(t("lines_invalid"));
    const payload = {
      po_number: form.po_number, vendor: form.vendor, order_date: form.order_date || null,
      expected_date: form.expected_date || null, status: form.status, notes: form.notes,
      lines: form.lines.map((l) => ({ line_id: l.line_id, item_type: l.item_type, item_id: l.item_id, qty: Number(l.qty) })),
    };
    try {
      if (editing) await api.put(`/pos/${initial.id}`, payload);
      else await api.post("/pos", payload);
      toast.success(t("saved"));
      onClose();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={!!initial} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-form-dialog">
        <DialogHeader><DialogTitle>{editing ? t("edit") : t("add")} - Purchase Order</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label={t("po_number")}><Input value={form.po_number} onChange={set("po_number")} data-testid="po-number-input" /></Field>
          <Field label={t("vendor")}><Input value={form.vendor || ""} onChange={set("vendor")} data-testid="po-vendor-input" /></Field>
          <Field label={t("status")}>
            <select className={selectCls} value={form.status === "cancelled" ? "cancelled" : "open"} onChange={set("status")} data-testid="po-status-select">
              <option value="open">{t("po_active")}</option>
              <option value="cancelled">{t("po_cancelled")}</option>
            </select>
          </Field>
          <Field label={t("order_date")}><Input type="date" value={form.order_date} onChange={set("order_date")} /></Field>
          <Field label={t("expected_date")}><Input type="date" value={form.expected_date} onChange={set("expected_date")} data-testid="po-expected-input" /></Field>
          <Field label={t("note")}><Input value={form.notes || ""} onChange={set("notes")} /></Field>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">{t("items_label")} ({form.lines.length})</Label>
          <div className="mt-1">
            <LineItemsEditor lines={form.lines} onChange={(lines) => setForm({ ...form, lines })} items={items} showStock showReceived={editing} testid="po-lines" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="po-save-btn">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
